/**
 * React hooks that wrap tRPC query options via `useSuspenseQuery`.
 *
 * Components import hooks from here; route loaders import `trpc` from
 * `@/lib/trpc` and use `trpc.*.queryOptions(...)` directly with
 * `queryClient.ensureQueryData(...)`.
 */

import { useSuspenseQuery } from '@tanstack/react-query';
import { portalLocaleExtendedToPortalLocale } from '@worldcoo/monolith/portals';
import type { categoriesKeys } from '@worldcoo/monolith/core';
import { notFound, redirect, useSearch, useRouteContext, useNavigate } from '@tanstack/react-router';
import { useEphemeralStore } from './app-store';
import { useDeferredValue, useEffect } from 'react';
import { trpc } from './trpc';

type CauseKey = (typeof categoriesKeys)[number];

// ── Helpers ──────────────────────────────────────────────────────────────────

function redirectToLogin(): never {
	const currentPath =
		typeof window !== 'undefined' ? window.location.pathname + window.location.search + window.location.hash : '';
	const relativePath = currentPath.startsWith('/ngos') ? currentPath.slice('/ngos'.length) : currentPath;
	throw redirect({
		to: '/ngos/login',
		search: { redirect: relativePath || undefined },
	});
}

// ── Portal hooks ─────────────────────────────────────────────────────────────

/**
 * Aggregates scoped to the current campaigns view (mirrors `usePortalCampaignsQuery`'s
 * filter inputs). Server returns aggregates only for the visible campaign ids;
 * `totalAggregate` is always the full portal sum.
 */
export function usePortalCampaignsAggregatesQuery() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang, city } = useSearch({ from: '__root__' });
	const { q, year, state, causes } = useSearch({ strict: false }) as {
		q?: string;
		year?: number;
		state?: 'finished';
		causes?: CauseKey[];
	};
	const deferredFilters = useDeferredValue({
		portalId,
		locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
		query: q,
		year,
		state,
		causes,
		city,
	});
	const { data } = useSuspenseQuery(trpc.portal.getCampaignsAggregates.queryOptions(deferredFilters));
	return data;
}

/**
 * Aggregates scoped to a single campaign (campaign detail page). Avoids shipping
 * the full portal aggregates dict for unrelated campaigns. The returned shape is
 * the same as the listing variant — `aggregates[campaignId]` + `totalAggregate`.
 */
export function usePortalCampaignAggregateQuery(campaignId: string) {
	const { portalId } = useRouteContext({ from: '__root__' });
	const { data } = useSuspenseQuery(
		trpc.portal.getCampaignsAggregates.queryOptions({ portalId, campaignIds: [campaignId] }),
	);
	return data;
}

export function usePortalAuthorQuery() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		trpc.portal.getAuthor.queryOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
		}),
	);
	if ('notFound' in data) return notFound();
	return data;
}

export function usePortalMetadataQuery() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		trpc.portal.getMetadata.queryOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
		}),
	);
	if ('notFound' in data) return notFound();
	return data;
}

export function usePortalCampaignMetadataQuery(campaignId: string) {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang } = useSearch({ from: '__root__' });
	const { data } = useSuspenseQuery(
		trpc.portal.getCampaignMetadata.queryOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
			campaignId,
		}),
	);
	if ('notFound' in data) return notFound();
	return data;
}

export function usePortalCampaignsQuery() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang, city } = useSearch({ from: '__root__' });
	const { q, year, state, causes } = useSearch({ from: '/$portalId/' });
	const deferredFilters = useDeferredValue({
		portalId,
		locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
		query: q,
		year,
		state,
		causes,
		city,
	});
	const { data } = useSuspenseQuery(trpc.portal.getCampaigns.queryOptions(deferredFilters));
	if ('notFound' in data) return notFound();
	return data;
}

export function usePortalCampaignQuery(campaignId: string) {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const { lang } = useSearch({ from: '__root__' });
	const navigate = useNavigate();
	const { data } = useSuspenseQuery(
		trpc.portal.getCampaign.queryOptions({
			portalId,
			locale: lang ? portalLocaleExtendedToPortalLocale[lang] : locale,
			campaignId,
		}),
	);
	const pagesLoaded = useEphemeralStore((store) => store.pagesLoaded);

	const redirectSlug = !('notFound' in data) ? data.redirectSlug : undefined;
	useEffect(() => {
		if (redirectSlug) {
			navigate({
				to: '/$portalId/$campaignId',
				params: { portalId, campaignId: redirectSlug },
				replace: true,
			});
		}
	}, [redirectSlug, portalId, navigate]);

	if ('notFound' in data) return notFound();
	return { data, isPageLoaded: pagesLoaded.includes(`campaign-${campaignId}`) };
}

// ── NGO hooks ────────────────────────────────────────────────────────────────

export function useNgoProjectsQuery(filters: {
	q: string | undefined;
	fundingType: 'myPortal' | 'worldcoo' | 'both' | undefined;
	status: 'draft' | 'active' | 'paused' | 'funded' | undefined;
}) {
	const deferredFilters = useDeferredValue({
		query: filters.q,
		fundingType: filters.fundingType,
		status: filters.status,
	});
	const { data } = useSuspenseQuery(trpc.ngo.getProjects.queryOptions(deferredFilters));
	if ('unauthenticated' in data) redirectToLogin();
	return data;
}

export function useNgoProjectQuery(projectId: string) {
	const { data } = useSuspenseQuery(trpc.ngo.getProject.queryOptions({ projectId }));
	if ('unauthenticated' in data) redirectToLogin();
	if ('notFound' in data) throw notFound();
	return data;
}

export function useNgoProfileQuery() {
	const { data } = useSuspenseQuery(trpc.ngo.getProfile.queryOptions());
	if ('unauthenticated' in data) redirectToLogin();
	if ('notFound' in data) throw notFound();
	return data;
}

export function useNgoPortalsQuery() {
	const { data } = useSuspenseQuery(trpc.ngo.getPortals.queryOptions());
	if ('unauthenticated' in data) redirectToLogin();
	return data;
}

export function useNgoPortalQuery(portalId: string) {
	const { data } = useSuspenseQuery(trpc.ngo.getPortal.queryOptions({ portalId }));
	if ('unauthenticated' in data) redirectToLogin();
	if ('notFound' in data) throw notFound();
	return data;
}

type NgoDashboardInput = {
	scope: 'all' | 'myPortal' | 'worldcoo';
	filter: {
		dateFrom?: string;
		dateTo?: string;
		campaignIds?: string[];
		merchantId?: string;
		channelBucket?: 'App' | 'Tpv' | 'Web';
		city?: string;
	};
	campaignsPage: number;
	campaignsPageSize: number;
	campaignsSort?: {
		key: 'name' | 'revenue' | 'funding' | 'status' | 'startDate' | 'endDate';
		order: 'asc' | 'desc';
	};
};

export function useNgoDashboardQuery(input: NgoDashboardInput) {
	const deferred = useDeferredValue(input);
	const { data } = useSuspenseQuery(trpc.ngo.getDashboard.queryOptions(deferred));
	if ('unauthenticated' in data) redirectToLogin();
	return data;
}
